/**
 * Service Agenda - Aligné avec la structure réelle de Supabase
 * Compatible avec otisite-dashboard
 */

import { supabase } from '@/lib/supabase'
import { icloudSyncService } from '@/services/icloudSync'
import type {
  CalendarAppointment,
  CalendarTask,
  AppointmentStatus,
  RendezVousStatut,
} from '@/types/agenda'

// ==================== TYPES ====================

export interface AgendaTimeSlot {
  id: string
  admin_id: string
  day_of_week: number // 0=Dimanche, 1=Lundi, ..., 6=Samedi
  start_time: string // HH:MM:SS
  end_time: string // HH:MM:SS
  is_active: boolean
  exceptions: string[] // ISO date strings for blackout dates
  created_at: string
  updated_at: string
}

export interface AgendaTimeSlotFormData {
  admin_id: string
  day_of_week: number
  start_time: string
  end_time: string
  is_active?: boolean
  exceptions?: string[]
}

export interface Creneau {
  id: string
  professionnel_id: string | null
  start: string // ISO 8601
  end_time: string // ISO 8601
  statut: 'libre' | 'reserve' | 'bloque'
  created_at: string
  updated_at: string
}

export interface RendezVous {
  id: string
  client_id: string | null
  prospect_id: string | null
  professionnel_id: string | null
  date_heure: string // ISO 8601
  description: string | null
  statut: 'en_attente' | 'confirme' | 'annule' | 'termine'
  created_at: string
  updated_at: string
  creneau_id: string | null
  confirmation_token?: string | null
  participants?: string[]
  guest_name?: string | null
  guest_email?: string | null
  guest_phone?: string | null
  source?: string | null
  // Relations (jointures)
  client?: {
    id: string
    email: string
    full_name: string | null
    company?: string | null
  } | null
  prospect?: {
    id: string
    raison_sociale: string | null
    nom_commercial: string | null
    contact_name: string | null
    email: string | null
  } | null
  professionnel?: {
    id: string
    email: string
    full_name: string | null
  }
}

export interface CreneauFormData {
  professionnel_id?: string
  start: string
  end_time: string
  statut?: 'libre' | 'reserve' | 'bloque'
}

export interface RendezVousFormData {
  client_id: string
  professionnel_id?: string
  date_heure: string
  description?: string
  statut?: 'en_attente' | 'confirme' | 'annule' | 'termine'
  creneau_id?: string
  participants?: string[]
}

export interface AgendaFilters {
  professionnelId?: string
  clientId?: string
  statut?: string
  dateFrom?: string
  dateTo?: string
}

export interface PastRendezVousFilters {
  dateFrom?: string
  dateTo?: string
  page?: number
  limit?: number
}

export interface AgendaStats {
  total: number
  en_attente: number
  confirme: number
  annule: number
  termine: number
  today: number
  thisWeek: number
}

// ==================== MAPPING HELPERS ====================

const statutFrToEn: Record<RendezVousStatut, AppointmentStatus> = {
  en_attente: 'pending',
  confirme: 'confirmed',
  annule: 'cancelled',
  termine: 'completed',
}

/**
 * Mappe un RendezVous DB vers un CalendarAppointment pour le CalendarView
 */
export function mapRendezVousToCalendar(rdv: RendezVous): CalendarAppointment {
  const prospectName = rdv.prospect
    ? rdv.prospect.contact_name
      || rdv.prospect.nom_commercial
      || rdv.prospect.raison_sociale
    : null

  return {
    id: `rdv-${rdv.id}`,
    type: 'appointment',
    start_datetime: rdv.date_heure,
    status: statutFrToEn[rdv.statut] || 'pending',
    client_name:
      rdv.client?.full_name
      || rdv.client?.email
      || prospectName
      || rdv.guest_name
      || rdv.guest_email
      || 'Client',
    client_email: rdv.client?.email || rdv.prospect?.email || rdv.guest_email || undefined,
    subject: rdv.description || 'Rendez-vous',
    description: rdv.description,
    rdv_id: rdv.id,
    statut_fr: rdv.statut,
    participants: rdv.participants,
  }
}

/**
 * Mappe une tâche projet vers un CalendarTask pour le CalendarView
 */
export function mapTaskToCalendar(task: {
  id: string
  title: string
  date: string
  priority: string
  status: string
  project_name: string
  project_id: string
}): CalendarTask {
  return {
    id: `task-${task.id}`,
    type: 'task',
    start_datetime: new Date(task.date + 'T09:00:00').toISOString(),
    status: task.status,
    title: task.title,
    project_name: task.project_name,
    project_id: task.project_id,
    priority: task.priority,
  }
}

// Select with client + prospect joins (RDV can be tied to either, or to a guest)
const RDV_SELECT_WITH_CLIENT = '*, client:users!client_id(id, email, full_name, company), prospect:prospects!prospect_id(id, raison_sociale, nom_commercial, contact_name, email)'

// ==================== SERVICE ====================

export const agendaService = {
  // ==================== CLIENTS/USERS ====================

  /**
   * Récupère ou crée un client par email
   */
  async getOrCreateClient(email: string, fullName?: string): Promise<{ id: string; email: string; full_name: string | null }> {
    // D'abord chercher si le user existe
    const { data: existingUser, error: searchError } = await supabase
      .from('users')
      .select('id, email, full_name')
      .eq('email', email)
      .maybeSingle()

    if (searchError) throw new Error(`Erreur recherche client: ${searchError.message}`)

    // Si le user existe, le retourner
    if (existingUser) {
      return existingUser
    }

    // Sinon, créer un nouveau user
    const { data: newUser, error: createError } = await supabase
      .from('users')
      .insert({
        email,
        full_name: fullName || null,
        role: 'client',
      })
      .select('id, email, full_name')
      .single()

    if (createError) throw new Error(`Erreur création client: ${createError.message}`)
    return newUser
  },

  /**
   * Recherche de clients léger (pour le sélecteur)
   */
  async searchClients(search: string, limit = 10): Promise<Array<{
    id: string
    email: string
    full_name: string | null
    company: string | null
  }>> {
    if (!search || search.length < 2) return []

    const { data, error } = await supabase
      .from('users')
      .select('id, email, full_name, company')
      .eq('role', 'client')
      .or(`full_name.ilike.%${search}%,email.ilike.%${search}%,company.ilike.%${search}%`)
      .limit(limit)

    if (error) {
      console.warn('Erreur recherche clients:', error)
      return []
    }
    return data || []
  },

  // ==================== CRÉNEAUX (agenda_time_slots) ====================

  /**
   * Récupère les créneaux récurrents d'un admin
   */
  async getTimeSlots(adminId: string): Promise<AgendaTimeSlot[]> {
    const { data, error } = await supabase
      .from('agenda_time_slots')
      .select('*')
      .eq('admin_id', adminId)
      .order('day_of_week', { ascending: true })
      .order('start_time', { ascending: true })

    if (error) throw new Error(`Erreur récupération créneaux: ${error.message}`)
    return data || []
  },

  /**
   * Crée un créneau récurrent
   */
  async createTimeSlot(data: AgendaTimeSlotFormData): Promise<AgendaTimeSlot> {
    const { data: slot, error } = await supabase
      .from('agenda_time_slots')
      .insert(data)
      .select()
      .single()

    if (error) throw new Error(`Erreur création créneau: ${error.message}`)
    return slot
  },

  /**
   * Met à jour un créneau récurrent
   */
  async updateTimeSlot(id: string, updates: Partial<AgendaTimeSlotFormData>): Promise<AgendaTimeSlot> {
    const { data, error } = await supabase
      .from('agenda_time_slots')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    if (error) {
      if (error.code === 'PGRST116') throw new Error('Créneau non trouvé')
      throw new Error(`Erreur mise à jour créneau: ${error.message}`)
    }
    return data
  },

  /**
   * Supprime un créneau récurrent
   */
  async deleteTimeSlot(id: string): Promise<void> {
    const { error } = await supabase
      .from('agenda_time_slots')
      .delete()
      .eq('id', id)

    if (error) throw new Error(`Erreur suppression créneau: ${error.message}`)
  },

  // ==================== CRÉNEAUX LEGACY (creneaux table) ====================

  async getCreneaux(filters?: {
    professionnelId?: string
    dateFrom?: string
    dateTo?: string
    statut?: string
    /**
     * Si true (défaut), n'inclut que les créneaux à venir (start > now()).
     * Mettre à false pour récupérer aussi les créneaux passés (vue
     * historique côté admin).
     */
    futureOnly?: boolean
  }): Promise<Creneau[]> {
    const futureOnly = filters?.futureOnly !== false
    let query = supabase
      .from('creneaux')
      .select('*')
      .order('start', { ascending: true })

    if (filters?.professionnelId) {
      query = query.or(`professionnel_id.eq.${filters.professionnelId},professionnel_id.is.null`)
    }
    if (filters?.dateFrom) {
      query = query.gte('start', filters.dateFrom)
    }
    if (filters?.dateTo) {
      query = query.lte('start', filters.dateTo)
    }
    if (filters?.statut) {
      query = query.eq('statut', filters.statut)
    }
    if (futureOnly) {
      query = query.gt('start', new Date().toISOString())
    }

    const { data, error } = await query

    if (error) throw new Error(`Erreur récupération créneaux: ${error.message}`)
    return data || []
  },

  async createCreneau(data: CreneauFormData): Promise<Creneau> {
    const { data: creneau, error } = await supabase
      .from('creneaux')
      .insert({
        ...data,
        statut: data.statut || 'libre',
      })
      .select()
      .single()

    if (error) throw new Error(`Erreur création créneau: ${error.message}`)
    return creneau
  },

  async updateCreneau(id: string, updates: Partial<CreneauFormData>): Promise<Creneau> {
    const { data, error } = await supabase
      .from('creneaux')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    if (error) {
      if (error.code === 'PGRST116') throw new Error('Créneau non trouvé')
      throw new Error(`Erreur mise à jour créneau: ${error.message}`)
    }
    return data
  },

  async deleteCreneau(id: string): Promise<void> {
    const { error } = await supabase
      .from('creneaux')
      .delete()
      .eq('id', id)

    if (error) throw new Error(`Erreur suppression créneau: ${error.message}`)
  },

  // ==================== RENDEZ-VOUS ====================

  /**
   * Récupère les rendez-vous avec filtres (avec jointure client)
   */
  async getRendezVous(filters?: AgendaFilters): Promise<{
    rendezVous: RendezVous[]
    total: number
  }> {
    let query = supabase
      .from('rendez_vous')
      .select(RDV_SELECT_WITH_CLIENT, { count: 'exact' })
      .order('date_heure', { ascending: true })

    if (filters?.professionnelId) {
      query = query.or(`professionnel_id.eq.${filters.professionnelId},professionnel_id.is.null`)
    }
    if (filters?.clientId) {
      query = query.eq('client_id', filters.clientId)
    }
    if (filters?.statut) {
      query = query.eq('statut', filters.statut)
    }
    if (filters?.dateFrom) {
      query = query.gte('date_heure', filters.dateFrom)
    }
    if (filters?.dateTo) {
      query = query.lte('date_heure', filters.dateTo)
    }

    const { data, error, count } = await query

    if (error) throw new Error(`Erreur récupération RDV: ${error.message}`)
    return {
      rendezVous: (data || []) as unknown as RendezVous[],
      total: count || 0,
    }
  },

  /**
   * Récupère un rendez-vous par ID (avec jointure client)
   */
  async getRendezVousById(id: string): Promise<RendezVous | null> {
    const { data, error } = await supabase
      .from('rendez_vous')
      .select(RDV_SELECT_WITH_CLIENT)
      .eq('id', id)
      .single()

    if (error) {
      if (error.code === 'PGRST116') return null
      throw new Error(`Erreur récupération RDV: ${error.message}`)
    }
    return data as unknown as RendezVous
  },

  /**
   * Crée un rendez-vous.
   * Le trigger SQL `trg_reserve_creneau_on_rdv_insert` se charge de
   * marquer automatiquement le créneau lié comme 'reserve' (via
   * creneau_id ou date_heure). Pas besoin d'updater creneaux ici.
   */
  async createRendezVous(data: RendezVousFormData): Promise<RendezVous> {
    const { data: rdv, error } = await supabase
      .from('rendez_vous')
      .insert({
        ...data,
        statut: data.statut || 'en_attente',
      })
      .select(RDV_SELECT_WITH_CLIENT)
      .single()

    if (error) throw new Error(`Erreur création RDV: ${error.message}`)

    const created = rdv as unknown as RendezVous
    icloudSyncService.upsert(created.id)
    return created
  },

  /**
   * Met à jour un rendez-vous
   */
  async updateRendezVous(id: string, updates: Partial<RendezVousFormData>): Promise<RendezVous> {
    const { data, error } = await supabase
      .from('rendez_vous')
      .update(updates)
      .eq('id', id)
      .select(RDV_SELECT_WITH_CLIENT)
      .single()

    if (error) {
      if (error.code === 'PGRST116') throw new Error('Rendez-vous non trouvé')
      throw new Error(`Erreur mise à jour RDV: ${error.message}`)
    }
    const updated = data as unknown as RendezVous
    if (updated.statut === 'annule') icloudSyncService.delete(updated.id)
    else icloudSyncService.upsert(updated.id)
    return updated
  },

  /**
   * Confirme un rendez-vous
   */
  async confirmerRendezVous(id: string): Promise<RendezVous> {
    return this.updateRendezVous(id, { statut: 'confirme' })
  },

  /**
   * Annule un rendez-vous.
   * Le trigger SQL `trg_release_creneau_on_rdv_cancel` libère
   * automatiquement le créneau lié. Pas besoin d'updater creneaux ici.
   */
  async annulerRendezVous(id: string): Promise<RendezVous> {
    return this.updateRendezVous(id, { statut: 'annule' })
  },

  /**
   * Supprime un rendez-vous.
   * Le DELETE ne déclenche pas le trigger sur statut, donc on libère
   * manuellement le créneau lié si nécessaire.
   */
  async deleteRendezVous(id: string): Promise<void> {
    const rdv = await this.getRendezVousById(id)

    const { error } = await supabase
      .from('rendez_vous')
      .delete()
      .eq('id', id)

    if (error) throw new Error(`Erreur suppression RDV: ${error.message}`)

    icloudSyncService.delete(id)

    // Si un créneau était lié et qu'aucun autre RDV non-annulé ne le
    // référence, le libérer
    if (rdv?.creneau_id) {
      await this.updateCreneau(rdv.creneau_id, { statut: 'libre' })
    }
  },

  // ==================== RDV PASSÉS ====================

  /**
   * Récupère les rendez-vous passés avec pagination
   */
  async getPastRendezVous(filters?: PastRendezVousFilters): Promise<{
    rendezVous: RendezVous[]
    total: number
  }> {
    const now = new Date().toISOString()
    const page = filters?.page || 1
    const limit = filters?.limit || 10
    const from = (page - 1) * limit
    const to = from + limit - 1

    let query = supabase
      .from('rendez_vous')
      .select(RDV_SELECT_WITH_CLIENT, { count: 'exact' })
      .lt('date_heure', now)
      .neq('statut', 'annule')
      .order('date_heure', { ascending: false })

    if (filters?.dateFrom) {
      query = query.gte('date_heure', filters.dateFrom)
    }
    if (filters?.dateTo) {
      query = query.lte('date_heure', filters.dateTo)
    }

    query = query.range(from, to)

    const { data, error, count } = await query

    if (error) throw new Error(`Erreur récupération RDV passés: ${error.message}`)
    return {
      rendezVous: (data || []) as unknown as RendezVous[],
      total: count || 0,
    }
  },

  // ==================== CONFIRMATION PAR TOKEN ====================

  /**
   * Récupère un RDV par son token de confirmation (accès public)
   */
  async getRendezVousByToken(token: string): Promise<RendezVous | null> {
    const { data, error } = await supabase
      .from('rendez_vous')
      .select(RDV_SELECT_WITH_CLIENT)
      .eq('confirmation_token', token)
      .single()

    if (error) {
      if (error.code === 'PGRST116') return null
      throw new Error(`Erreur récupération RDV par token: ${error.message}`)
    }
    return data as unknown as RendezVous
  },

  /**
   * Confirme un RDV via token (accès public)
   */
  async confirmRendezVousByToken(token: string): Promise<RendezVous> {
    const { data, error } = await supabase
      .from('rendez_vous')
      .update({ statut: 'confirme' })
      .eq('confirmation_token', token)
      .eq('statut', 'en_attente')
      .select(RDV_SELECT_WITH_CLIENT)
      .single()

    if (error) {
      if (error.code === 'PGRST116') throw new Error('Rendez-vous non trouvé ou déjà traité')
      throw new Error(`Erreur confirmation RDV: ${error.message}`)
    }
    return data as unknown as RendezVous
  },

  /**
   * Décline un RDV via token (accès public)
   */
  async declineRendezVousByToken(token: string): Promise<RendezVous> {
    const { data, error } = await supabase
      .from('rendez_vous')
      .update({ statut: 'annule' })
      .eq('confirmation_token', token)
      .eq('statut', 'en_attente')
      .select(RDV_SELECT_WITH_CLIENT)
      .single()

    if (error) {
      if (error.code === 'PGRST116') throw new Error('Rendez-vous non trouvé ou déjà traité')
      throw new Error(`Erreur annulation RDV: ${error.message}`)
    }
    return data as unknown as RendezVous
  },

  // ==================== EMAIL CONFIRMATION ====================

  /**
   * Construit le HTML de l'email de confirmation de RDV
   */
  buildAppointmentConfirmationEmail(rdv: RendezVous, appUrl: string): { subject: string; html: string } {
    const token = rdv.confirmation_token
    const dateFormatted = new Date(rdv.date_heure).toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })

    const confirmUrl = `${appUrl}/rdv/confirm/${token}`
    const declineUrl = `${appUrl}/rdv/decline/${token}`

    const subject = `Confirmation de rendez-vous - ${dateFormatted}`

    const html = `
<!DOCTYPE html>
<html lang="fr">
<head><meta charset="UTF-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"></head>
<body style="margin:0;padding:0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;background-color:#f5f3ef;">
  <div style="max-width:600px;margin:0 auto;padding:40px 20px;">
    <div style="background:white;border-radius:12px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,0.08);">
      <div style="background:#4ade80;padding:24px 32px;">
        <h1 style="color:white;margin:0;font-size:20px;">Rendez-vous à confirmer</h1>
      </div>
      <div style="padding:32px;">
        <p style="color:#44403c;font-size:16px;line-height:1.6;margin-top:0;">
          Bonjour,
        </p>
        <p style="color:#44403c;font-size:16px;line-height:1.6;">
          Un rendez-vous a été planifié pour vous :
        </p>
        <div style="background:#f5f3ef;border-radius:8px;padding:20px;margin:24px 0;">
          <p style="margin:0 0 8px;color:#78716c;font-size:14px;">Date et heure</p>
          <p style="margin:0 0 16px;color:#1c1917;font-size:16px;font-weight:600;">${dateFormatted}</p>
          ${rdv.description ? `
          <p style="margin:0 0 8px;color:#78716c;font-size:14px;">Description</p>
          <p style="margin:0;color:#1c1917;font-size:16px;">${rdv.description}</p>
          ` : ''}
        </div>
        <p style="color:#44403c;font-size:16px;line-height:1.6;">
          Merci de confirmer ou décliner ce rendez-vous :
        </p>
        <div style="text-align:center;margin:32px 0;">
          <a href="${confirmUrl}" style="display:inline-block;background:#4ade80;color:white;text-decoration:none;padding:12px 32px;border-radius:8px;font-weight:600;font-size:16px;margin-right:12px;">
            Confirmer
          </a>
          <a href="${declineUrl}" style="display:inline-block;background:#d6d3d1;color:#44403c;text-decoration:none;padding:12px 32px;border-radius:8px;font-weight:600;font-size:16px;">
            Décliner
          </a>
        </div>
      </div>
      <div style="border-top:1px solid #e7e5e4;padding:16px 32px;text-align:center;">
        <p style="color:#a8a29e;font-size:12px;margin:0;">LesSablesWeb - CRM</p>
      </div>
    </div>
  </div>
</body>
</html>`

    return { subject, html }
  },

  /**
   * Construit le HTML de l'email envoyé au client quand l'admin
   * accepte sa demande de rendez-vous.
   * Aligné sur les design tokens du worker (services/emailTemplates.ts).
   */
  buildAppointmentAcceptedEmail(rdv: RendezVous): { subject: string; html: string } {
    const escapeHtml = (str: unknown): string => {
      if (typeof str !== 'string') return ''
      return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#x27;')
    }

    const dateFormatted = new Date(rdv.date_heure).toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })

    // Design tokens (cf. workers/src/services/emailTemplates.ts)
    const STYLE = {
      heading: '#5B4A3F',
      subtitle: '#8B7D6B',
      body: '#6B5D4F',
      muted: '#A09485',
      cardBg: '#ffffff',
      cardBorder: '#E8E0D4',
      infoBg: '#faf9f7',
      successBorder: '#4CAFA4',
      font: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    }
    const h2 = `color: ${STYLE.heading}; font-size: 20px; margin: 0 0 16px;`
    const p = `color: ${STYLE.body}; font-size: 15px; line-height: 1.6; margin: 0 0 24px;`
    const small = `color: ${STYLE.muted}; font-size: 13px; line-height: 1.5; margin: 24px 0 0;`
    const label = `margin: 4px 0; color: ${STYLE.body}; font-size: 14px;`

    const clientName = escapeHtml(rdv.client?.full_name)
    const description = rdv.description ? escapeHtml(rdv.description) : null
    const dateLabel = escapeHtml(dateFormatted)

    const subject = `Votre rendez-vous est confirmé - ${dateFormatted}`

    const highlight = `<div style="background: ${STYLE.infoBg}; border-left: 4px solid ${STYLE.successBorder}; padding: 15px; margin: 20px 0; border-radius: 6px;">
        <p style="${label}"><strong>Date et heure :</strong> ${dateLabel}</p>
        ${description ? `<p style="${label}"><strong>Description :</strong> ${description}</p>` : ''}
      </div>`

    const html = `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Rendez-vous confirm&eacute;</title>
</head>
<body style="margin: 0; padding: 0; background-color: #f5f2ee;">
  <div style="font-family: ${STYLE.font}; max-width: 560px; margin: 0 auto; padding: 40px 20px;">
    <div style="text-align: center; margin-bottom: 32px;">
      <h1 style="color: ${STYLE.heading}; font-size: 24px; margin: 0;">Les Sables Web</h1>
      <p style="color: ${STYLE.subtitle}; font-size: 13px; margin: 4px 0 0;">Votre partenaire digital</p>
    </div>
    <div style="background: ${STYLE.cardBg}; border: 1px solid ${STYLE.cardBorder}; border-radius: 12px; padding: 32px;">
      <h2 style="${h2}">Rendez-vous confirm&eacute;</h2>
      <p style="${p}">Bonjour ${clientName || 'cher client'}, bonne nouvelle : votre demande de rendez-vous a bien &eacute;t&eacute; accept&eacute;e.</p>
      ${highlight}
      <p style="${p}">Nous nous r&eacute;jouissons de vous retrouver &agrave; cette date. En cas d'impr&eacute;vu, n'h&eacute;sitez pas &agrave; nous contacter pour reprogrammer.</p>
      <p style="${small}">&Agrave; tr&egrave;s bient&ocirc;t !</p>
    </div>
    <div style="text-align: center; margin-top: 32px;">
      <p style="color: ${STYLE.muted}; font-size: 12px; margin: 0;">&copy; ${new Date().getFullYear()} Les Sables Web</p>
    </div>
  </div>
</body>
</html>`

    return { subject, html }
  },

  /**
   * Construit le HTML de l'email envoyé au client quand l'admin
   * annule son rendez-vous.
   * Aligné sur les design tokens du worker (services/emailTemplates.ts).
   */
  buildAppointmentCancelledEmail(rdv: RendezVous): { subject: string; html: string } {
    const escapeHtml = (str: unknown): string => {
      if (typeof str !== 'string') return ''
      return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#x27;')
    }

    const dateFormatted = new Date(rdv.date_heure).toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })

    const STYLE = {
      heading: '#5B4A3F',
      subtitle: '#8B7D6B',
      body: '#6B5D4F',
      muted: '#A09485',
      cardBg: '#ffffff',
      cardBorder: '#E8E0D4',
      infoBg: '#faf9f7',
      warningBorder: '#d97706',
      font: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    }
    const h2 = `color: ${STYLE.heading}; font-size: 20px; margin: 0 0 16px;`
    const p = `color: ${STYLE.body}; font-size: 15px; line-height: 1.6; margin: 0 0 24px;`
    const small = `color: ${STYLE.muted}; font-size: 13px; line-height: 1.5; margin: 24px 0 0;`
    const label = `margin: 4px 0; color: ${STYLE.body}; font-size: 14px;`

    const clientName = escapeHtml(rdv.client?.full_name)
    const description = rdv.description ? escapeHtml(rdv.description) : null
    const dateLabel = escapeHtml(dateFormatted)

    const subject = `Annulation de votre rendez-vous - ${dateFormatted}`

    const highlight = `<div style="background: ${STYLE.infoBg}; border-left: 4px solid ${STYLE.warningBorder}; padding: 15px; margin: 20px 0; border-radius: 6px;">
        <p style="${label}"><strong>Date et heure :</strong> ${dateLabel}</p>
        ${description ? `<p style="${label}"><strong>Description :</strong> ${description}</p>` : ''}
      </div>`

    const html = `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Annulation de rendez-vous</title>
</head>
<body style="margin: 0; padding: 0; background-color: #f5f2ee;">
  <div style="font-family: ${STYLE.font}; max-width: 560px; margin: 0 auto; padding: 40px 20px;">
    <div style="text-align: center; margin-bottom: 32px;">
      <h1 style="color: ${STYLE.heading}; font-size: 24px; margin: 0;">Les Sables Web</h1>
      <p style="color: ${STYLE.subtitle}; font-size: 13px; margin: 4px 0 0;">Votre partenaire digital</p>
    </div>
    <div style="background: ${STYLE.cardBg}; border: 1px solid ${STYLE.cardBorder}; border-radius: 12px; padding: 32px;">
      <h2 style="${h2}">Rendez-vous annul&eacute;</h2>
      <p style="${p}">Bonjour ${clientName || 'cher client'}, nous vous informons que votre rendez-vous a &eacute;t&eacute; annul&eacute;.</p>
      ${highlight}
      <p style="${p}">D&eacute;sol&eacute; pour la g&ecirc;ne occasionn&eacute;e. N'h&eacute;sitez pas &agrave; nous recontacter pour reprogrammer un nouveau cr&eacute;neau qui vous conviendra.</p>
      <p style="${small}">Merci pour votre compr&eacute;hension.</p>
    </div>
    <div style="text-align: center; margin-top: 32px;">
      <p style="color: ${STYLE.muted}; font-size: 12px; margin: 0;">&copy; ${new Date().getFullYear()} Les Sables Web</p>
    </div>
  </div>
</body>
</html>`

    return { subject, html }
  },

  // ==================== STATISTIQUES ====================

  /**
   * Récupère les statistiques de l'agenda
   */
  async getStats(professionnelId?: string): Promise<AgendaStats> {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const tomorrow = new Date(today)
    tomorrow.setDate(tomorrow.getDate() + 1)
    const nextWeek = new Date(today)
    nextWeek.setDate(nextWeek.getDate() + 7)

    let query = supabase
      .from('rendez_vous')
      .select('statut, date_heure')

    if (professionnelId) {
      query = query.or(`professionnel_id.eq.${professionnelId},professionnel_id.is.null`)
    }

    const { data, error } = await query

    if (error) throw new Error(`Erreur stats: ${error.message}`)

    const rdvs = data || []

    return {
      total: rdvs.length,
      en_attente: rdvs.filter(r => r.statut === 'en_attente').length,
      confirme: rdvs.filter(r => r.statut === 'confirme').length,
      annule: rdvs.filter(r => r.statut === 'annule').length,
      termine: rdvs.filter(r => r.statut === 'termine').length,
      today: rdvs.filter(r => {
        const d = new Date(r.date_heure)
        return d >= today && d < tomorrow
      }).length,
      thisWeek: rdvs.filter(r => {
        const d = new Date(r.date_heure)
        return d >= today && d < nextWeek
      }).length,
    }
  },

  /**
   * Récupère les RDV du jour (avec jointure client)
   */
  async getTodayRendezVous(professionnelId?: string): Promise<RendezVous[]> {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const tomorrow = new Date(today)
    tomorrow.setDate(tomorrow.getDate() + 1)

    let query = supabase
      .from('rendez_vous')
      .select(RDV_SELECT_WITH_CLIENT)
      .gte('date_heure', today.toISOString())
      .lt('date_heure', tomorrow.toISOString())
      .order('date_heure', { ascending: true })

    if (professionnelId) {
      query = query.or(`professionnel_id.eq.${professionnelId},professionnel_id.is.null`)
    }

    const { data, error } = await query

    if (error) throw new Error(`Erreur RDV du jour: ${error.message}`)
    return (data || []) as unknown as RendezVous[]
  },

  /**
   * Récupère les RDV en attente (avec jointure client)
   */
  async getRendezVousEnAttente(professionnelId?: string): Promise<RendezVous[]> {
    let query = supabase
      .from('rendez_vous')
      .select(RDV_SELECT_WITH_CLIENT)
      .eq('statut', 'en_attente')
      .order('date_heure', { ascending: true })

    if (professionnelId) {
      query = query.or(`professionnel_id.eq.${professionnelId},professionnel_id.is.null`)
    }

    const { data, error } = await query

    if (error) throw new Error(`Erreur RDV en attente: ${error.message}`)
    return (data || []) as unknown as RendezVous[]
  },

  // ==================== TASKS INTEGRATION ====================

  /**
   * Get tasks due on specific dates for calendar display
   * Note: Uses project_phases as project_tasks table doesn't exist
   */
  async getTasksForCalendar(filters?: {
    dateFrom?: string
    dateTo?: string
    assignedTo?: string
  }): Promise<Array<{
    id: string
    title: string
    date: string
    type: 'task'
    priority: string
    status: string
    project_name: string
    project_id: string
  }>> {
    try {
      let query = supabase
        .from('project_phases')
        .select(`
          id,
          name,
          end_date,
          status,
          project_id,
          project:projects(title)
        `)
        .not('end_date', 'is', null)

      if (filters?.dateFrom) {
        query = query.gte('end_date', filters.dateFrom)
      }
      if (filters?.dateTo) {
        query = query.lte('end_date', filters.dateTo)
      }
      // Note: assignedTo filter not supported in project_phases

      const { data, error } = await query

      if (error) {
        console.warn('Error fetching tasks for calendar:', error)
        return []
      }

      return (data || []).map(phase => ({
        id: phase.id,
        title: phase.name,
        date: phase.end_date,
        type: 'task' as const,
        priority: 'medium',
        status: phase.status || 'pending',
        project_name: (phase as any).project?.title || 'Projet inconnu',
        project_id: phase.project_id,
      }))
    } catch {
      console.warn('Table project_phases not available for calendar')
      return []
    }
  },
}

export default agendaService
